import { SolomonLogomark } from "@shared/components/Brand/SolomonLogomark";
import {
    ArrowShortUpIcon,
    WalletIcon
} from "@solomon-labs/design/components/icons";
import React, { type CSSProperties, useEffect, useRef, useState } from "react";

// Original token/USDv artwork selected from the design-system asset manifest.
import usdv from "../../../../../../../.claude/design-system/assets/tokens/USDv.png";

interface Point {
    x: number;
    y: number;
}
interface Holder {
    id: string;
    point: Point;
    holdsUsdv: boolean;
    coinCount: number;
    delay: number;
}
interface LaneStyle extends CSSProperties {
    "--destination-x": string;
    "--destination-y": string;
    "--lane-delay": string;
    "--receive-animation": string;
}

// Illustration coordinates, not UI dimensions. The grid, tile centers and
// routes all share SecurityMap's projection; every stop is a grid intersection.
const ISO_X = 0.82;
const ISO_Y = 0.482;
const GRID_STEP = 40;
const ORIGIN: Point = { x: 320, y: 82 };
const project = ({ x, y }: Point): Point => ({
    x: ORIGIN.x + (x - y) * ISO_X,
    y: ORIGIN.y + (x + y) * ISO_Y
});
const HOLDER_DISTANCE = GRID_STEP * 5;
const HOLDERS: readonly Holder[] = [
    {
        id: "left",
        point: { x: 0, y: HOLDER_DISTANCE },
        holdsUsdv: true,
        coinCount: 3,
        delay: 0
    },
    {
        id: "right",
        point: { x: HOLDER_DISTANCE, y: 0 },
        holdsUsdv: true,
        coinCount: 2,
        delay: 0.4
    },
    {
        id: "bottom",
        point: { x: HOLDER_DISTANCE, y: HOLDER_DISTANCE },
        holdsUsdv: false,
        coinCount: 0,
        delay: 0
    }
];
const QUALIFIED_HOLDERS = HOLDERS.filter(({ holdsUsdv }) => holdsUsdv);
const PLANE = `matrix(${ISO_X} ${-ISO_Y} ${ISO_X} ${ISO_Y} 0 0)`;
const TILE_HALF = 32;
const TILE_X = TILE_HALF * 2 * ISO_X;
const TILE_Y = TILE_HALF * 2 * ISO_Y;
const TILE_DEPTH = 7;
const TOP = `M0 ${-TILE_Y}L${TILE_X} 0 0 ${TILE_Y} ${-TILE_X} 0Z`;
const FRONT = `M${-TILE_X} 0L0 ${TILE_Y} ${TILE_X} 0V${TILE_DEPTH}L0 ${TILE_Y + TILE_DEPTH} ${-TILE_X} ${TILE_DEPTH}Z`;
const COIN_RADIUS = 14 * 0.75;
const COIN_X = Math.SQRT2 * ISO_X * COIN_RADIUS;
const COIN_Y = Math.SQRT2 * ISO_Y * COIN_RADIUS;
const COIN_DEPTH = 5 * 0.85 * 0.85 * 0.75;
const HELD_COIN_RADIUS = 14 * 1.5 * 0.85;
const COIN_STAGGER_SECONDS = 0.45;
const CYCLE_SECONDS = 9;
const TRAVEL_START = 23;
const TRAVEL_END = 53;
const FADE_END = 57;
// First contact of the raised coin face with the wallet's top edge. Projecting
// its lift back onto either grid axis moves the contact point by depth/(2*ISO_Y).
const ENTRY_PROGRESS =
    (HOLDER_DISTANCE - TILE_HALF - COIN_RADIUS + COIN_DEPTH / (2 * ISO_Y)) /
    HOLDER_DISTANCE;
const ENTRY_AT = TRAVEL_START + (TRAVEL_END - TRAVEL_START) * ENTRY_PROGRESS;
const ARROW_ENTER_END = ENTRY_AT + (150 / 9000) * 100;

const REWARDS_MOTION_CSS = `
.distribution-rewards-empty {
  --rewards-paper: rgb(var(--color-surface-ground));
  --rewards-coin-side: rgb(var(--color-surface-raised));
  --rewards-tint: rgb(var(--color-surface-tint-ground));
  --rewards-tint-side: rgb(var(--color-surface-tint-sunken));
  /* A patient illustrative sequence, separate from UI response timings. */
  --rewards-cycle: ${CYCLE_SECONDS}s;
  --rewards-linear: cubic-bezier(0, 0, 1, 1);
}
.distribution-rewards-grid { opacity: .065; }
.distribution-rewards-routes { opacity: .4; }
.distribution-rewards-packet {
  opacity: 0;
  animation: distribution-rewards-travel var(--rewards-cycle) var(--rewards-linear) var(--lane-delay) infinite;
}
.distribution-rewards-received {
  opacity: 0;
  animation: var(--receive-animation) var(--rewards-cycle) var(--rewards-linear) var(--lane-delay) infinite;
}
.distribution-rewards-empty[data-paused='true'] * { animation-play-state: paused; }
/* The coin's bottom stays on the route; only its face is lifted by the rim. */
@keyframes distribution-rewards-travel {
  0%, 20% { transform: translate(${ORIGIN.x}px, ${ORIGIN.y}px); opacity: 0; }
  ${TRAVEL_START}% { transform: translate(${ORIGIN.x}px, ${ORIGIN.y}px); opacity: 1; }
  ${TRAVEL_END}% { transform: translate(var(--destination-x), var(--destination-y)); opacity: 1; }
  ${FADE_END}%, 100% { transform: translate(var(--destination-x), var(--destination-y)); opacity: 0; }
}
${QUALIFIED_HOLDERS.map(({ id, coinCount }) => {
    // One arrival signal per wallet: appear with the first coin, hold through
    // the train, and fade with the final coin rather than blinking per payout.
    const tail =
        (((coinCount - 1) * COIN_STAGGER_SECONDS) / CYCLE_SECONDS) * 100;
    return `@keyframes distribution-rewards-receive-${id} {
      0%, ${ENTRY_AT}% { opacity: 0; }
      ${ARROW_ENTER_END}%, ${TRAVEL_END + tail}% { opacity: 1; }
      ${FADE_END + tail}%, 100% { opacity: 0; }
    }`;
}).join("\n")}
@media (prefers-reduced-motion: reduce) {
  .distribution-rewards-empty * { animation: none !important; }
  .distribution-rewards-received { opacity: 1; }
}
`;

function UsdvCoin(): React.ReactElement {
    return (
        <g className="text-line-base" data-usdv-coin>
            <path
                d={`M${-COIN_X} ${-COIN_DEPTH}A${COIN_X} ${COIN_Y} 0 0 0 ${COIN_X} ${-COIN_DEPTH}V0A${COIN_X} ${COIN_Y} 0 0 1 ${-COIN_X} 0Z`}
                fill="var(--rewards-coin-side)"
                stroke="currentColor"
                strokeWidth={0.7 * 0.75}
            />
            <g transform={`translate(0 ${-COIN_DEPTH})`}>
                <g transform={PLANE}>
                    <image
                        href={usdv}
                        x={-COIN_RADIUS}
                        y={-COIN_RADIUS}
                        width={COIN_RADIUS * 2}
                        height={COIN_RADIUS * 2}
                    />
                </g>
            </g>
        </g>
    );
}

function Platform({
    source = false,
    layer
}: {
    source?: boolean;
    layer: "sides" | "top";
}): React.ReactElement {
    return (
        <g className="text-tint-base" stroke="currentColor" strokeWidth="0.7">
            {layer === "sides" ? (
                <>
                    <path
                        d={FRONT}
                        fill={
                            source
                                ? "var(--rewards-tint-side)"
                                : "var(--rewards-paper)"
                        }
                    />
                    <path
                        d={`M0 ${TILE_Y}V${TILE_Y + TILE_DEPTH}`}
                        fill="none"
                    />
                </>
            ) : (
                <path
                    d={TOP}
                    fill={
                        source ? "var(--rewards-tint)" : "var(--rewards-paper)"
                    }
                />
            )}
        </g>
    );
}

function laneStyle({ id, point, delay }: Holder, coinIndex = 0): LaneStyle {
    const destination = project(point);
    return {
        "--destination-x": `${destination.x}px`,
        "--destination-y": `${destination.y}px`,
        "--lane-delay": `${delay + coinIndex * COIN_STAGGER_SECONDS}s`,
        "--receive-animation": `distribution-rewards-receive-${id}`
    };
}

/** Upcoming payouts go only to the two USDv holders; the third wallet stays idle. */
export function DistributionRewardsAnimation(): React.ReactElement {
    const root = useRef<HTMLElement>(null);
    const [active, setActive] = useState(true);
    useEffect(() => {
        const element = root.current;
        if (!element) return;
        let inView = true;
        const sync = (): void => setActive(inView && !document.hidden);
        const observer =
            typeof IntersectionObserver === "undefined"
                ? undefined
                : new IntersectionObserver(([entry]) => {
                      inView = entry.isIntersecting;
                      sync();
                  });
        observer?.observe(element);
        document.addEventListener("visibilitychange", sync);
        sync();
        return (): void => {
            observer?.disconnect();
            document.removeEventListener("visibilitychange", sync);
        };
    }, []);
    return (
        <figure
            ref={root}
            className="distribution-rewards-empty relative m-0 mb-4 w-full max-w-xl text-foreground-base"
            data-paused={!active}
        >
            <style dangerouslySetInnerHTML={{ __html: REWARDS_MOTION_CSS }} />
            <svg
                className="distribution-rewards-scene block h-auto w-full overflow-hidden"
                viewBox="70 0 500 350"
                role="img"
                aria-label="Illustration of upcoming rewards: Three USDv coins travel from Solomon to the left wallet and two to the right wallet. Both hold USDv and show green up-arrows. The bottom wallet holds no USDv and receives no payout."
            >
                <g
                    className="distribution-rewards-grid"
                    stroke="currentColor"
                    strokeWidth="0.6"
                    fill="none"
                    aria-hidden="true"
                >
                    {Array.from({ length: 17 }, (_, i) => {
                        const coordinate = (i - 8) * GRID_STEP;
                        const a = project({ x: coordinate, y: -400 });
                        const b = project({ x: coordinate, y: 600 });
                        const c = project({ x: -400, y: coordinate });
                        const d = project({ x: 600, y: coordinate });
                        return (
                            <React.Fragment key={coordinate}>
                                <path d={`M${a.x} ${a.y}L${b.x} ${b.y}`} />
                                <path d={`M${c.x} ${c.y}L${d.x} ${d.y}`} />
                            </React.Fragment>
                        );
                    })}
                </g>
                <g
                    className="distribution-rewards-routes text-tint-base"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                >
                    {QUALIFIED_HOLDERS.map(({ id, point }) => {
                        const destination = project(point);
                        return (
                            <path
                                key={id}
                                data-payout-route={id}
                                d={`M${ORIGIN.x} ${ORIGIN.y}L${destination.x} ${destination.y}`}
                            />
                        );
                    })}
                </g>
                {/* SVG painter order: routes, sidewalls, moving coins, then
                    every tile top. Coins emerge from and enter under the lids. */}
                <g
                    data-wallet-connector="bottom"
                    className="distribution-rewards-routes text-tint-base"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                >
                    <path
                        d={`M${ORIGIN.x} ${ORIGIN.y}V${project({ x: HOLDER_DISTANCE, y: HOLDER_DISTANCE }).y}`}
                    />
                </g>
                <g data-paint-layer="sidewalls">
                    <g transform={`translate(${ORIGIN.x} ${ORIGIN.y})`}>
                        <Platform source layer="sides" />
                    </g>
                    {HOLDERS.map(({ id, point }) => {
                        const destination = project(point);
                        return (
                            <g
                                key={id}
                                transform={`translate(${destination.x} ${destination.y})`}
                            >
                                <Platform layer="sides" />
                            </g>
                        );
                    })}
                </g>
                <g data-paint-layer="coins">
                    {QUALIFIED_HOLDERS.flatMap((holder) =>
                        Array.from({ length: holder.coinCount }, (_, index) => (
                            <g
                                key={`${holder.id}-${index}`}
                                style={laneStyle(holder, index)}
                                className="distribution-rewards-packet"
                                data-payout-coin={holder.id}
                            >
                                <UsdvCoin />
                            </g>
                        ))
                    )}
                </g>
                <g data-paint-layer="tile-tops">
                    <g transform={`translate(${ORIGIN.x} ${ORIGIN.y})`}>
                        <Platform source layer="top" />
                        <g transform={PLANE}>
                            <rect
                                x="-23"
                                y="-23"
                                width="46"
                                height="46"
                                rx="3"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1"
                                className="text-foreground-inverse"
                            />
                            <foreignObject
                                x="-16"
                                y="-16"
                                width="32"
                                height="32"
                            >
                                <SolomonLogomark className="size-full text-foreground-inverse" />
                            </foreignObject>
                        </g>
                    </g>
                    {HOLDERS.map((holder) => {
                        const { id, point, holdsUsdv } = holder;
                        const destination = project(point);
                        return (
                            <g
                                key={id}
                                style={laneStyle(holder)}
                                data-rewards-holder={id}
                                data-holds-usdv={holdsUsdv}
                            >
                                <g
                                    transform={`translate(${destination.x} ${destination.y})`}
                                >
                                    <Platform layer="top" />
                                    <g transform={PLANE}>
                                        <WalletIcon
                                            x={-18}
                                            y={-18}
                                            width={36}
                                            height={36}
                                            className="text-tint-base"
                                        />
                                    </g>
                                    {holdsUsdv && (
                                        <>
                                            <g
                                                transform="translate(-36 10)"
                                                data-held-usdv
                                            >
                                                <image
                                                    href={usdv}
                                                    x={-HELD_COIN_RADIUS}
                                                    y={-HELD_COIN_RADIUS}
                                                    width={HELD_COIN_RADIUS * 2}
                                                    height={
                                                        HELD_COIN_RADIUS * 2
                                                    }
                                                />
                                            </g>
                                            <g
                                                className="distribution-rewards-received text-success-base"
                                                transform="translate(24 -22)"
                                                data-reward-received
                                            >
                                                <ArrowShortUpIcon
                                                    width={20}
                                                    height={20}
                                                />
                                            </g>
                                        </>
                                    )}
                                </g>
                            </g>
                        );
                    })}
                </g>
            </svg>
        </figure>
    );
}
